/**
 * Separators used between a container's name label (prefix + auto-number +
 * display suffix) and its heading text (`text` / title body).
 *
 * Distinct from {@link DEFAULT_AUTO_NUMBER_SEPARATOR}, which joins tokens
 * *inside* the name label.
 */
export const HeadingTitleSeparator = {
  EmDash: ' — ',
  Dot: '. ',
  None: ' ',
} as const;

const TopLevelParentStyles = new Set([
  'part',
  'appendix',
  'book',
  'subsection',
]);

const isTopLevelParent = (parentStyle: string | null | undefined): boolean =>
  parentStyle == null || TopLevelParentStyles.has(parentStyle);

/**
 * Resolves the name-label ↔ heading-text separator for a container block.
 *
 * Policy:
 * - `part` / `appendix` → em dash
 * - top-level `section` → dot (parent is part/appendix/book/subsection/null)
 * - nested `section` → space
 * - `rule` / `article` / `definition` → space
 * - other → space
 *
 * When `override` is non-null, it wins (future per-block dynamic separator).
 */
export const resolveHeadingTitleSeparator = ({
  override,
  parentStyle,
  style,
}: {
  style: string;
  parentStyle?: string | null;
  /** Future per-block value; when non-null, wins. */
  override?: string | null;
}): string => {
  if (override != null) {
    return override;
  }

  if (style === 'part' || style === 'appendix') {
    return HeadingTitleSeparator.EmDash;
  }

  if (style === 'rule' || style === 'article' || style === 'definition') {
    return HeadingTitleSeparator.None;
  }

  if (style === 'section') {
    return isTopLevelParent(parentStyle)
      ? HeadingTitleSeparator.Dot
      : HeadingTitleSeparator.None;
  }

  return HeadingTitleSeparator.None;
};

/**
 * Joins a name label and heading title with `separator` only when both sides
 * are non-empty after trim — avoids trailing separators like `Part I —`.
 */
export const joinNameAndTitle = (
  nameLabel: string,
  title: string,
  separator: string,
): string => {
  const nameToken = nameLabel.trim();
  const titleToken = title.trim();
  if (nameToken.length === 0) {
    return titleToken;
  }
  if (titleToken.length === 0) {
    return nameToken;
  }
  return `${nameToken}${separator}${titleToken}`;
};
